import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { UniversityResult } from '../../shared/models/university-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the University Finder, mirroring {@link FlyingClubFinderResultsListingComponent}. Renders
 * the paged result cards (each linking to the existing `/business/:id` detail page), a filter popover, and
 * relays hover events for map sync.
 */
@Component({
  selector: 'app-university-finder-results-listing',
  templateUrl: './university-finder-results-listing.component.html',
  styleUrls: ['./university-finder-results-listing.component.scss']
})
export class UniversityFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() universities: Array<UniversityResult>;
  @Input() state: State = State.Empty;
  @Input() activeUniversity: UniversityResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalUniversities = 0;
  @Output() universityMouseEnter = new EventEmitter<UniversityResult>();
  @Output() universityMouseLeave = new EventEmitter<UniversityResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('universityListEl') universityListEl: ElementRef;
  @ViewChildren('universityEl', { read: ElementRef }) universityEls: QueryList<ElementRef>;
  @ViewChild('universityFiltersPopover') universityFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to a university's detail page, so that page's
   * "Back" button can return to these exact results (see BusinessPageComponent.goBack). Also clears the
   * airport back-target so the university opened from search doesn't fall back to "Back to <airport>".
   */
  onUniversitySelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onUniversityHover(university: UniversityResult, hover: boolean) {
    if (hover) {
      this.universityMouseEnter.emit(university);
    } else {
      this.universityMouseLeave.emit(university);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given university.
   */
  getUniversityEl(university: UniversityResult) {
    const i = this.universities.indexOf(university);
    if (i < 0) {
      return null;
    }
    const el = this.universityEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified university (used for map hover sync).
   */
  public scrollToUniversity(university: UniversityResult) {
    if (!this.universityListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.universityListEl.nativeElement);
    const $el = this.getUniversityEl(university);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.universityListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.universityListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
